/**
 * Diccionario de traducción de permisos técnicos a nombres amigables en español.
 * Organizado por categorías para agrupamiento visual.
 */

export interface PermissionInfo {
  label: string;
  category: PermissionCategory;
  icon?: string;
}

export type PermissionCategory = "maps" | "newsletters" | "management_roles" | "management_actions" | "management_view" | "subscriptions" | "purchases" | "other";

export const PERMISSION_CATEGORIES: Record<PermissionCategory, { label: string; color: string; bgColor: string }> = {
  maps: {
    label: "Mapas",
    color: "#1677ff",
    bgColor: "#e6f4ff",
  },
  newsletters: {
    label: "Boletines",
    color: "#52c41a",
    bgColor: "#f6ffed",
  },
  management_roles: {
    label: "Gestionar roles y permisos",
    color: "#8c8c8c",
    bgColor: "#fafafa",
  },
  management_actions: {
    label: "Gestión de usuarios – Acciones directas sobre usuarios",
    color: "#8c8c8c",
    bgColor: "#fafafa",
  },
  management_view: {
    label: "Visualización",
    color: "#8c8c8c",
    bgColor: "#fafafa",
  },
  subscriptions: {
    label: "Suscripciones (Admin)",
    color: "#722ed1",
    bgColor: "#f9f0ff",
  },
  purchases: {
    label: "Compras e Historial",
    color: "#13c2c2",
    bgColor: "#e6fffb",
  },
  other: {
    label: "Otros",
    color: "#d48806",
    bgColor: "#fffbe6",
  },
};

export const PERMISSION_LABELS: Record<string, PermissionInfo> = {
  // Mapas
  "ui:map": { label: "Acceso a mapas", category: "maps" },
  "ui:map:point": { label: "Mapas por punto", category: "maps" },
  "ui:map:transect": { label: "Análisis por transecto", category: "maps" },

  // Boletines — acceso raíz
  "ui:newsletter": { label: "Acceso a boletines", category: "newsletters" },

  // Perú (separado desde el inicio)
  "ui:newsletter:peru_1": { label: "Perú — Boletín 1", category: "newsletters" },
  "ui:newsletter:peru_2": { label: "Perú — Boletín v3", category: "newsletters" },
  // Legacy: acceso general a Perú (antes de la separación _1/_2)
  "ui:newsletter:peru": { label: "Perú — Acceso general legacy", category: "newsletters" },

  // Ecuador
  "ui:newsletter:ecuador_2": { label: "Ecuador — Boletín v3", category: "newsletters" },
  "ui:newsletter:ecuador": { label: "Ecuador — Boletín 1", category: "newsletters" },

  // Omán
  "ui:newsletter:oman_2": { label: "Omán — Boletín v3", category: "newsletters" },
  "ui:newsletter:oman": { label: "Omán — Boletín 1", category: "newsletters" },

  // Panamá
  "ui:newsletter:panama_2": { label: "Panamá — Boletín v3", category: "newsletters" },
  "ui:newsletter:panama": { label: "Panamá — Boletín 1", category: "newsletters" },

  // Gestión de usuarios
  "ui:roles:manage": { label: "Gestionar roles", category: "management_roles" },
  "ui:users:update:permissions": { label: "Gestionar permisos de empleados", category: "management_roles" },
  "ui:users": { label: "Gestión de usuarios", category: "management_roles" },
  "ui:data:manage": { label: "Gestión total de datos", category: "management_roles" },
  "ui:system:config": { label: "Configuración del Sistema", category: "management_roles" },

  // Acciones directas sobre usuarios
  "ui:users:create": { label: "Crear usuarios", category: "management_actions" },
  "ui:users:update": { label: "Editar usuarios", category: "management_actions" },
  "ui:users:delete": { label: "Eliminar usuarios", category: "management_actions" },
  "ui:users:disable": { label: "Habilitar/Deshabilitar usuarios", category: "management_actions" },

  // Visualización
  "ui:dashboard:view": { label: "Ver dashboard", category: "management_view" },
  "ui:profile:view": { label: "Ver perfil", category: "management_view" },
  "ui:users:view": { label: "Ver usuarios", category: "management_view" },
  "users:view_all": { label: "Ver todos los usuarios", category: "management_view" },
  "users:view_company": { label: "Ver usuarios de empresa", category: "management_view" },
  "ui:roles:view": { label: "Ver roles", category: "management_view" },

  // Suscripciones y Planes (Admin)
  "ui:subscriptions": { label: "Acceso a suscripciones", category: "subscriptions" },
  "ui:subscription-plans:view": { label: "Ver planes", category: "subscriptions" },
  "ui:subscription-plans:create": { label: "Crear planes", category: "subscriptions" },
  "ui:subscription-plans:update": { label: "Editar planes", category: "subscriptions" },
  "ui:subscription-plans:delete": { label: "Eliminar planes", category: "subscriptions" },
  "ui:subscriptions:manage:all": { label: "Gestión total de suscripciones", category: "subscriptions" },
  "ui:subscriptions:assign": { label: "Asignar plan a empresa", category: "subscriptions" },
  "ui:subscriptions:toggle": { label: "Activar/Desactivar suscripción", category: "subscriptions" },
  "ui:subscriptions:delete": { label: "Eliminar suscripción", category: "subscriptions" },

  // Compras y facturación (Empresa / Cliente)
  "ui:subscriptions:view": { label: "Ver mis planes", category: "purchases" },
  "ui:subscriptions:manage": { label: "Gestionar mi plan", category: "purchases" },
  "ui:purchases:view": { label: "Ver mis compras", category: "purchases" },
  "ui:purchases:manage": { label: "Realizar compras", category: "purchases" },

  // Vistas compartidas (Eliminadas las visualizaciones repetidas aquí arriba)

  // Sistema (Asignación interna de roles)
  "ui:users:update:type": { label: "Modificar estructura de roles", category: "other" },
  "ui:users:update:type:admin": { label: "Otorgar rol: Administrador", category: "other" },
  "ui:users:update:type:gestor": { label: "Otorgar rol: Gestor", category: "other" },
  "ui:users:update:type:empresa": { label: "Otorgar rol: Empresa", category: "other" },
  "ui:users:update:type:empleado": { label: "Otorgar rol: Empleado", category: "other" },
};

/**
 * Obtener la etiqueta amigable de un permiso.
 * Si no existe en el diccionario, devuelve el slug formateado.
 */
export function getPermissionLabel(slug: string): string {
  return PERMISSION_LABELS[slug]?.label || slug.replace(/^ui:/, "").replace(/:/g, " › ");
}

/**
 * Obtener la categoría de un permiso.
 */
export function getPermissionCategory(slug: string): PermissionCategory {
  return PERMISSION_LABELS[slug]?.category || "other";
}

/**
 * Agrupar un array de permisos (slugs) por categoría.
 */
export function groupPermissionsByCategory(
  permissions: string[]
): Record<PermissionCategory, string[]> {
  const grouped: Record<PermissionCategory, string[]> = {
    maps: [],
    newsletters: [],
    management_roles: [],
    management_actions: [],
    management_view: [],
    subscriptions: [],
    purchases: [],
    other: [],
  };

  permissions.forEach((slug) => {
    const cat = getPermissionCategory(slug);
    grouped[cat].push(slug);
  });

  return grouped;
}
